import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Fab,
  Tooltip,
  Snackbar,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  VpnKey as VpnKeyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API_URL } from '../config';

interface Organization {
  id: number;
  name: string;
  slug: string;
  status: string;
  created_at: string;
}

interface CreateOrganizationResult {
  id: number;
  slug: string;
  client_key: string;
  embed_snippet: string;
  api_token: string;
}

const Organizations: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [result, setResult] = useState<CreateOrganizationResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  const [regenerateTarget, setRegenerateTarget] = useState<Organization | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratedToken, setRegeneratedToken] = useState<string | null>(null);
  const [regeneratedTokenCopied, setRegeneratedTokenCopied] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/organizations/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrganizations(response.data);
    } catch (error) {
      showSnackbar('Erro ao carregar organizações', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.is_platform_admin !== true) {
      navigate('/login');
      return;
    }
    fetchOrganizations();
  }, [isAuthenticated, user, navigate, fetchOrganizations]);

  const resetForm = () => {
    setName('');
    setSlug('');
    setSlugError('');
    setDomainInput('');
    setDomains([]);
    setResult(null);
    setCopied(false);
    setTokenCopied(false);
  };

  const handleOpenDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleAddDomain = () => {
    const trimmed = domainInput.trim();
    if (trimmed && !domains.includes(trimmed)) {
      setDomains([...domains, trimmed]);
    }
    setDomainInput('');
  };

  const handleDomainKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setDomains(domains.filter((d) => d !== domain));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSlugError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/organizations/`,
        { name, slug, domains },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      fetchOrganizations();
    } catch (error: any) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Erro ao criar organização';
      if (status === 400) {
        setSlugError(detail);
      } else {
        showSnackbar(detail, 'error');
      }
    }
    setSubmitting(false);
  };

  const handleCopySnippet = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.embed_snippet);
    setCopied(true);
  };

  const handleCopyToken = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.api_token);
    setTokenCopied(true);
  };

  const handleFinish = () => {
    setOpenDialog(false);
    resetForm();
    showSnackbar('Organização criada com sucesso!', 'success');
  };

  const handleOpenRegenerate = (org: Organization) => {
    setRegenerateTarget(org);
    setRegeneratedToken(null);
    setRegeneratedTokenCopied(false);
  };

  const handleCloseRegenerate = () => {
    setRegenerateTarget(null);
    setRegeneratedToken(null);
    setRegeneratedTokenCopied(false);
  };

  const handleConfirmRegenerate = async () => {
    if (!regenerateTarget) return;
    setRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/organizations/${regenerateTarget.id}/api-token/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegeneratedToken(response.data.api_token);
    } catch (error) {
      showSnackbar('Erro ao regenerar token de API', 'error');
      setRegenerateTarget(null);
    }
    setRegenerating(false);
  };

  const handleCopyRegeneratedToken = async () => {
    if (!regeneratedToken) return;
    await navigator.clipboard.writeText(regeneratedToken);
    setRegeneratedTokenCopied(true);
  };

  if (loading && organizations.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Organizações
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Crie e gerencie as organizações-cliente da plataforma
        </Typography>
      </Box>

      {/* Tabela de Organizações */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Organização</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criada em</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <BusinessIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        Nenhuma organização cadastrada ainda
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                organizations.map((org) => (
                  <TableRow key={org.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon color="primary" sx={{ mr: 1.5 }} />
                        <Typography variant="body1" fontWeight="medium">
                          {org.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                        {org.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={org.status === 'active' ? 'Ativa' : org.status}
                        color={org.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(org.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Regenerar token de API">
                        <IconButton size="small" onClick={() => handleOpenRegenerate(org)}>
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Botão FAB para adicionar */}
      <Tooltip title="Nova organização">
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={handleOpenDialog}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Dialog de Criação */}
      <Dialog open={openDialog} onClose={result ? undefined : handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{result ? 'Organização criada' : 'Nova organização'}</DialogTitle>

        {!result ? (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Nome"
                fullWidth
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Slug"
                fullWidth
                variant="outlined"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  if (slugError) setSlugError('');
                }}
                required
                error={Boolean(slugError)}
                helperText={slugError || 'Identificador único, ex.: cliente-novo'}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" sx={{ mb: 1 }}>
                Domínios (opcional)
              </Typography>
              <TextField
                margin="dense"
                label="Adicionar domínio"
                placeholder="exemplo.com.br"
                fullWidth
                variant="outlined"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={handleDomainKeyDown}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button onClick={handleAddDomain} disabled={!domainInput.trim()}>
                        Adicionar
                      </Button>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5 }}
              />

              {domains.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {domains.map((domain) => (
                    <Chip
                      key={domain}
                      label={domain}
                      onDelete={() => handleRemoveDomain(domain)}
                      size="small"
                    />
                  ))}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? <CircularProgress size={20} /> : 'Criar'}
              </Button>
            </DialogActions>
          </form>
        ) : (
          <>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 3 }}>
                Organização <strong>{name}</strong> criada com sucesso. A configuração do
                chatbot foi clonada automaticamente.
              </Alert>

              <Typography variant="body2" sx={{ mb: 1 }}>
                Código de instalação (adicione na landing page do cliente):
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  position: 'relative',
                }}
              >
                {result.embed_snippet}
                <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                  <IconButton
                    onClick={handleCopySnippet}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.100' }}
                  >
                    {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ mb: 1.5 }}>
                Token de API secreto -- use pra integração servidor-a-servidor (ex.: ZeloIA).
                Só aparece aqui, uma vez. Guarde agora; se perder, gere um novo na tabela.
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'warning.50',
                  borderColor: 'warning.main',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  position: 'relative',
                }}
              >
                {result.api_token}
                <Tooltip title={tokenCopied ? 'Copiado!' : 'Copiar'}>
                  <IconButton
                    onClick={handleCopyToken}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    {tokenCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Paper>

              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Slug: <strong>{result.slug}</strong>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleFinish} variant="contained">
                Concluir
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog de regenerar token de API */}
      <Dialog
        open={Boolean(regenerateTarget)}
        onClose={regeneratedToken ? undefined : handleCloseRegenerate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{regeneratedToken ? 'Token regenerado' : 'Regenerar token de API'}</DialogTitle>
        {!regeneratedToken ? (
          <>
            <DialogContent>
              <Alert severity="warning" icon={<WarningIcon fontSize="small" />}>
                Isso invalida imediatamente o token atual de <strong>{regenerateTarget?.name}</strong>.
                Qualquer integração usando o token antigo (ex.: ZeloIA) para de funcionar até
                você atualizar com o token novo.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRegenerate}>Cancelar</Button>
              <Button onClick={handleConfirmRegenerate} variant="contained" color="warning" disabled={regenerating}>
                {regenerating ? <CircularProgress size={20} /> : 'Regenerar mesmo assim'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                Token de <strong>{regenerateTarget?.name}</strong> regenerado com sucesso.
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'warning.50',
                  borderColor: 'warning.main',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  position: 'relative',
                }}
              >
                {regeneratedToken}
                <Tooltip title={regeneratedTokenCopied ? 'Copiado!' : 'Copiar'}>
                  <IconButton
                    onClick={handleCopyRegeneratedToken}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    {regeneratedTokenCopied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Paper>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRegenerate} variant="contained">
                Concluir
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Organizations;
